import { get } from 'lodash-es';
import { DROTypeEnum, NUM_RATIO, ProductTypeEnum, UnitEnum } from '/@/enums/exchangeServiceEnum';
import { defineStore } from 'pinia';
import { store } from '/@/store';
//import { ProductItemType } from '/@/api/exchangeService/model/product';
import { createLocalStorage } from '/@/utils/cache';
import { ETP_BUY_RECORD_KEY } from '/@/enums/cacheEnum';
import { buildUUID } from '/@/utils/uuid';
import { useAccountInfosStore } from './account';
import { EtpDetailType, MarketType } from '/@/api/exchangeService/model/productEtp';
import { useRouter } from 'vue-router';
import { useBecomeMember } from '/@/hooks/component/useBecomeMember';
import {
  etpInfoApi,
  etpInfoMarketApi,
  fetchAccountProductHold,
  fetchProductExtendInfo,
  fetchRate,
  queryLoopInvestApi,
  submitAoOrder,
  submitSoOrder,
} from '/@/api/exchangeService';
import {
  AoOrderItem,
  SoOrderItem,
  SoTradeDirection,
  SubmitSoOrderParams,
} from '/@/api/exchangeService/model/trade';
import { HoldingListItem } from '/@/api/exchangeService/model/holding';
import { useI18n } from '@mcex/locale';
import BigNumber from 'bignumber.js';
import { ExtendInfoItem } from '/@/api/exchangeService/model/product';

export enum OperationType {
  // 查看详情
  viewDetail = 'viewDetail',
  // 持仓中
  holding = 'holding',
  // 挂牌转让
  listingTransfer = 'listingTransfer',
  // 申请回售
  applyResale = 'applyResale',
}
export enum TradingType {
  // 购买
  buying = 'buying',
  // 转让
  sell = 'sell',
}
type OperationFromType = {
  num: string;
  userPrice: string;
  loopInvest?: boolean;
  loopInvestOrigin?: boolean;
  loopInvestEdit?: boolean;
};

const ls = createLocalStorage();

interface BuyingState {
  operartType: Nullable<OperationType>;
  tradingType: Nullable<TradingType>;
  form: OperationFromType;
  uuid: string;
  etpInfo: Nullable<EtpDetailType>;
  marketInfo: Nullable<MarketType>;
  holdingInfo: Nullable<HoldingListItem>;
  feeRate: Record<DROTypeEnum, number>;
  feeRateChanged: boolean;
  /** 当前交易类型 */
  currentType: DROTypeEnum;
  exInfo: ExtendInfoItem[];
  /** 剩余份额 */
  availableShare: Record<string, number>;
}
/**
 * Etp交易 store
 */
export const useTradEtpStore = defineStore({
  id: 'etp-trade-form',
  state: (): BuyingState => ({
    operartType: null,
    tradingType: null,
    form: {
      num: '',
      userPrice: '',
      loopInvest: undefined,
    },
    uuid: buildUUID(),
    etpInfo: null,
    marketInfo: null,
    holdingInfo: null,
    feeRateChanged: false,
    feeRate: {
      [DROTypeEnum.IO]: 0,
      [DROTypeEnum.AO]: 0,
      [DROTypeEnum.SO]: 0,
    },
    currentType: DROTypeEnum.IO,
    exInfo: [],
    availableShare: {},
  }),
  getters: {
    getEtpType(): DROTypeEnum {
      return this.currentType || DROTypeEnum.SO;
    },

    getOperartType(): OperationType {
      // operartType不存在则从缓存中获取
      if (!this.operartType) {
        const operartType = ls.get(`${ETP_BUY_RECORD_KEY}-operartType`) || OperationType.viewDetail;
        this.operartType = operartType;
      }
      return this.operartType as OperationType;
    },

    getShowAccountSelect(): boolean {
      return this.getOperartType !== OperationType.holding;
    },

    getTradingType(): TradingType {
      // tradingType不存在则从缓存中获取
      if (!this.tradingType) {
        const tradingType = ls.get(`${ETP_BUY_RECORD_KEY}-tradingType`) || TradingType.buying;
        this.tradingType = tradingType;
      }
      return this.tradingType as TradingType;
    },

    /**
     * 购买手数*换算比例
     * @returns {BigNumber} 总交易份额
     */
    getTradeTotalOrigin(): BigNumber {
      const num = this.form.num === '' ? 0 : this.form.num;
      const sum = new BigNumber(num).multipliedBy(NUM_RATIO);
      return sum;
    },

    /**
     * 购买手数*换算比例
     * @returns 总交易份额格式化后
     */
    getTradeTotal(): string {
      return this.getTradeTotalOrigin.toFormat(0);
    },

    /**
     *
     * @returns 价格
     */
    getPrice(): string {
      // 初始化用户报价1
      let userPrice: Nullable<string> = this.etpInfo?.referPrice
        ? String(this.etpInfo?.referPrice)
        : '1';
      // SO时，用户报价为etpInfo中的申购价格
      if (this.getEtpType === DROTypeEnum.SO) {
        userPrice = this.form.userPrice || '0';
      }
      return BigNumber(userPrice).toFixed(4);
    },
    /**
     *
     * @returns 手续费率
     * 向上取整 保留5位小数
     */
    getFeeRate(): string {
      // 如果手续费率发生变化，则取最新的手续费率
      let rate = get(this.etpInfo, 'etpTradeRule.tradeFee', 0);
      if (this.feeRateChanged) {
        rate = this.feeRate[this.getEtpType];
      } else if ([DROTypeEnum.IO, DROTypeEnum.AO].includes(this.getEtpType)) {
        rate = get(this.etpInfo, 'etpTradeRule.ipoFee', 0);
      }
      return BigNumber(rate).toFixed(5, BigNumber.ROUND_UP);
    },

    /**
     *
     * @returns {BigNumber} 手续费
     * roundMode:  四舍五入保留2位小数
     */
    getTradeFeeOrigin(): BigNumber {
      const sum = new BigNumber(this.form.num || 0)
        .multipliedBy(NUM_RATIO)
        .multipliedBy(this.getPrice)
        .multipliedBy(this.getFeeRate);
      return BigNumber(sum.toFixed(2));
    },

    /**
     *
     * @returns 手续费 格式化值
     */
    getTradeFee(): string {
      return this.getTradeFeeOrigin.toFormat(2);
    },

    /**
     * 购买手数*换算比例*价格 不包含手续费
     * roundMode:  四舍五入保留2位小数
     * @returns {BigNumber} 总交易金额
     */
    getTradeTotalAmountOrigin(): BigNumber {
      const sum = new BigNumber(this.form.num || 0)
        .multipliedBy(NUM_RATIO)
        .multipliedBy(this.getPrice);
      return BigNumber(sum.toFixed(2));
    },

    /**
     * 购买手数*换算比例*价格 不包含手续费
     * @returns 总交易金额 格式化值
     */
    getTradeTotalAmount(): string {
      return this.getTradeTotalAmountOrigin.toFormat(2);
    },

    /**
     * 申购价格（价格*申购份额 + 费用）之和
     * @returns 总支付金额
     */
    getTotalPayAmountOrigin(): BigNumber {
      if (this.getTradingType === TradingType.sell) {
        return this.getTradeTotalAmountOrigin.minus(this.getTradeFeeOrigin);
      }
      return this.getTradeTotalAmountOrigin.plus(this.getTradeFeeOrigin);
    },

    /**
     * 申购价格（价格*申购份额 + 费用）之和
     * @returns 总支付金额格式化
     */
    getTotalPayAmount(): string {
      return this.getTotalPayAmountOrigin.toFormat(2);
    },
    /**
     *
     * @returns 持仓信息
     */
    getHoldingInfo(): Nullable<HoldingListItem> {
      //holdingInfo不存在则从缓存中获取
      if (!this.holdingInfo) {
        const holdingInfo = ls.get(`${ETP_BUY_RECORD_KEY}-holdingInfo`);
        this.holdingInfo = holdingInfo;
      }
      return this.holdingInfo;
    },
    /**
     * 获取交易单位
     */
    getTradeUnit(): string {
      const { t } = useI18n();

      return t(
        `mcex.product.tradeUnit.${
          UnitEnum[this.etpInfo?.etpTradeRule?.tradeUnit ?? UnitEnum.SHARES]
        }`,
      );
    },
    isTranChe(): boolean {
      return this.etpInfo?.etpBaseInfo?.tranche === 0;
    },
    isTheFifthProduct(): boolean {
      return this.etpInfo?.productCode === '0005.MO';
    },
    // 是否是8号产品
    isTheEighthProduct(): boolean {
      return this.etpInfo?.productCode === '0008.MO';
    },
  },
  actions: {
    // 重置uuid
    resetUuid() {
      this.uuid = buildUUID();
    },
    /**
     * 重置form
     */
    resetForm() {
      this.form.num = '';
      this.form.userPrice = '';
      this.form.loopInvest = undefined;
    },

    /**
     * 设置操作类型
     */
    setOperartType(type: OperationType) {
      this.operartType = type;
      // 保存在本地
      ls.set(`${ETP_BUY_RECORD_KEY}-operartType`, type);
    },

    /**
     * 设置交易类型
     */
    setTradingType(type: TradingType) {
      this.tradingType = type;
      // 保存在本地
      ls.set(`${ETP_BUY_RECORD_KEY}-tradingType`, type);
    },

    /**
     * 设置etp详情
     * @param etpInfo {EtpDetailType} etp详情
     */
    setEtpInfo(etpInfo: EtpDetailType) {
      this.etpInfo = {
        ...this.etpInfo,
        ...etpInfo,
      };
    },

    /**
     * 设置持仓详情
     * @param holdingInfo {HoldingListItem} 持仓详情
     */
    setHoldingInfo(holdingInfo: HoldingListItem) {
      this.holdingInfo = holdingInfo;
      ls.set(`${ETP_BUY_RECORD_KEY}-holdingInfo`, holdingInfo);
    },
    /**
     * 设置购买列表
     * @param form {OperationFromType} 购买列表
     */
    setBuyinForm(form: OperationFromType) {
      this.form = form;
      // 保存在本地
      ls.set(ETP_BUY_RECORD_KEY, form);
    },

    /**
     * 提交申购 默认使用选中的账户，如果是挂牌转让则使用持仓中的账户
     * @returns
     */
    async submit() {
      const accountInfosStore = useAccountInfosStore();

      let tradingAccountCode = accountInfosStore.currentAccount?.tradingAccountCode;
      let orderList: (SoOrderItem | AoOrderItem)[] = [];
      if (this.getEtpType === DROTypeEnum.SO) {
        orderList = [
          {
            refCode: this.uuid,
            productCode: this.etpInfo?.productCode || '',
            totalShares: this.getTradeTotalOrigin.toFixed(),
            orderDirection:
              this.getTradingType === TradingType.sell
                ? SoTradeDirection.Sell
                : SoTradeDirection.Buy,
            bidPrice: this.getPrice,
          },
        ];
        if (
          this.getOperartType === OperationType.holding &&
          this.getTradingType === TradingType.sell
        ) {
          tradingAccountCode = this.getHoldingInfo?.tradingAccountCode || tradingAccountCode;
        }
        const submitParams = {
          tradingAccountCode,
          source: 'MT',
          txId: this.uuid,
          orderList,
        };
        return submitSoOrder(submitParams as SubmitSoOrderParams);
      } else {
        orderList = [
          {
            refCode: this.uuid,
            productCode: this.etpInfo?.productCode || '',
            buyShares: this.getTradeTotalOrigin.toFixed(),
          },
        ];
        const submitDatas = {
          tradingAccountCode,
          source: 'MT',
          txId: this.uuid,
          orderList,
        };
        if (
          this.form.loopInvest === undefined ||
          this.form.loopInvest === this.form.loopInvestOrigin
        ) {
          return submitAoOrder(submitDatas);
        }
        return submitAoOrder(submitDatas, { loopInvest: this.form.loopInvest });
      }
    },

    async fetchEtpData(productCode: string | null = null, drpType: string | null = null) {
      const router = useRouter();
      const { currentRoute } = router ?? {};
      if (!productCode) {
        productCode = currentRoute?.value.params.productCode as string;
      }
      this.fetchMarket(productCode);
      const res = await etpInfoApi({ productCode });

      if (!drpType) {
        drpType = currentRoute?.value.params.drpType as string;
      }
      // etpInfo中的canIo为true,etpInfo中的offeringStage为So
      if (res.offeringStage === DROTypeEnum.SO && res.canIo) {
        // 获取手续费率
        this.fetchTradeFee(DROTypeEnum.IO);
      }
      // 获取手续费率
      this.fetchTradeFee(res.offeringStage);
      this.setEtpInfo(res);
    },

    async fetchTradeFee(type) {
      const { isLogin, notBecomeMember } = useBecomeMember();
      if (!isLogin.value || notBecomeMember.value) {
        return;
      }
      // 获取费率
      const res = await fetchRate({ offeringStage: type, productType: ProductTypeEnum.DRP });
      // 从缓存中获取
      this.feeRate[type] = res;
    },

    /**
     * 查询是否开启循环投资
     */
    async fetchLoopInvest() {
      if (!this.etpInfo?.etpBaseInfo?.loopInvest) {
        return;
      }
      const accountInfosStore = useAccountInfosStore();
      const tradingAccountCode = accountInfosStore.currentAccount?.tradingAccountCode;
      let productCode = this.etpInfo?.productCode;
      if (!productCode) {
        const router = useRouter();
        const { currentRoute } = router;
        productCode = currentRoute.value.params.productCode as string;
      }
      const res = await queryLoopInvestApi({
        tradingAccountCode,
        productCode,
      });
      this.form.loopInvest = !!res.status;
      this.form.loopInvestOrigin = !!res.status;
      this.form.loopInvestEdit = res.allowChangeLoopInvest;
    },

    /**
     * 获取市场信息
     */
    async fetchMarket(productCode) {
      const res = await etpInfoMarketApi({ productCode });
      const referPrice = res.referPrice;
      this.marketInfo = res;
      this.setEtpInfo({ referPrice } as EtpDetailType);
    },

    /**
     * 获取产品扩展信息
     */
    async fetchProductExInfo(productCode, offeringStage: DROTypeEnum) {
      const res = await fetchProductExtendInfo({ productCode, offeringStage });
      this.exInfo = res;
    },

    /**
     * 获取账户当前剩余份额
     */
    async fetchAvailableShare(tradingAccountCode) {
      const res = await fetchAccountProductHold({
        tradingAccountCode,
        productCode: this.etpInfo?.productCode ?? '',
      });
      this.availableShare[tradingAccountCode] = res?.availableShare ?? 0;
    },
  },
});

// Need to be used outside the setup
export function useTradEtpStoreWithOut() {
  return useTradEtpStore(store);
}
